import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg, fmtDate } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";

export const UsersPage = () => {
  const call = useAdminApi();
  const [data, reload] = useLoad("/users");
  const change = async (u, role) => {
    try {
      await call("patch", `/users/${u.user_id}/role`, { role });
      toast.success("Role updated");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHead title="Admin users & roles" />
      <p className="mb-4 max-w-3xl text-sm text-slate-600">Anyone who signs in starts as <strong>member</strong> with no access. Grant <strong>staff</strong> (manage applications & orders) or <strong>admin</strong> (everything incl. vacancies, products, refunds, users).</p>
      <DataTable head={["Email", "User ID", "Role"]} testid="users-table">
        {data?.users.map((u) => (
          <tr key={u.user_id}><Td>{u.email}</Td><Td className="font-mono-num text-xs">{u.user_id}</Td><Td><NativeSelect value={u.role} onChange={(r) => change(u, r)} options={data.roles} testid={`user-role-${u.user_id}`} /></Td></tr>
        ))}
      </DataTable>
    </div>
  );
};

export const AuditPage = () => {
  const [logs] = useLoad("/admin/audit-logs");
  const [notes] = useLoad("/admin/notification-logs");
  return (
    <div>
      <PageHead title="Audit logs" />
      <DataTable head={["Date", "Admin", "Action", "Entity", "Details"]} testid="audit-table">
        {logs?.map((l) => <tr key={l.id}><Td className="text-xs">{fmtDate(l.created_at, true)}</Td><Td className="text-xs">{l.actor_email}</Td><Td className="font-semibold">{l.action}</Td><Td className="font-mono-num text-xs">{l.entity}: {l.entity_id}</Td><Td className="max-w-md break-all text-xs">{JSON.stringify(l.details)}</Td></tr>)}
      </DataTable>
      <h2 className="mb-3 mt-10 font-display text-xl font-bold">Email & notification log</h2>
      <DataTable head={["Date", "Channel", "To", "Template", "Status", "Error"]} testid="notification-table">
        {notes?.map((n) => <tr key={n.id}><Td className="text-xs">{fmtDate(n.created_at, true)}</Td><Td>{n.channel}</Td><Td className="text-xs">{n.to}</Td><Td>{n.template}</Td><Td>{n.status}</Td><Td className="text-xs">{n.error}</Td></tr>)}
      </DataTable>
    </div>
  );
};

const FIELDS = [["address", "Registered / office address"], ["phone", "Phone"], ["email", "Email"], ["whatsapp", "WhatsApp"], ["business_hours", "Business hours"], ["grievance_officer", "Grievance officer"]];

const FeeCard = ({ current }) => {
  const call = useAdminApi();
  const [amount, setAmount] = useState(String((current || 25000) / 100));
  const save = async () => {
    const n = Number(amount);
    if (!n || n < 1 || n > 10000) return toast.error("Fee must be between ₹1 and ₹10,000");
    if (!window.confirm(`Change the job application fee to ₹${amount}? New payments will be charged this amount.`)) return;
    try {
      await call("put", "/admin/settings/fee", { amount: Number(amount) });
      toast.success(`Application fee set to ₹${amount}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mb-8 space-y-3 rounded-2xl border-2 border-amber-500 bg-white p-6" data-testid="fee-settings-card">
      <h2 className="font-display text-lg font-bold">Job application fee</h2>
      <div className="flex items-end gap-2">
        <div className="space-y-1"><Label>Fee (₹, whole rupees)</Label><Input type="number" min={1} max={10000} className="w-40" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} data-testid="settings-fee-input" /></div>
        <Button onClick={save} disabled={!amount || Number(amount) < 1} data-testid="settings-fee-save-button">Save fee</Button>
      </div>
      <p className="text-xs text-slate-500">Updates the fee notice, pay button, policy page and Razorpay amount for new payments immediately. Already-paid applications keep their original receipt amount.</p>
    </div>
  );
};

export const SettingsPage = () => {
  const call = useAdminApi();
  const [s, setS] = useState(null);
  useEffect(() => {
    call("get", "/settings").then(setS);
  }, [call]);
  const save = async () => {
    try {
      await call("put", "/admin/settings", Object.fromEntries(FIELDS.map(([k]) => [k, s[k] || ""])));
      toast.success("Contact details saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!s) return null;
  return (
    <div className="max-w-2xl">
      <FeeCard current={s.application_fee_paise} />
      <PageHead title="Official business contact details" />
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
        {FIELDS.map(([k, l]) => <div key={k} className="space-y-1"><Label>{l}</Label><Input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}-input`} /></div>)}
        <Button onClick={save} data-testid="settings-save-button">Save</Button>
        <p className="text-xs text-slate-500">Shown on Contact Us and the footer. Do not enter registration numbers or approvals you cannot verify.</p>
      </div>
    </div>
  );
};
